import React from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import logo from "../images/LogoNavbar1.svg";
import logo2 from "../images/Recurso 7.svg";

// ===== Brand Tokens (pueden salir de tu theme) =====
const BRAND_ORANGE = "#F35A3A";
const BRAND_DARK = "#19242F";
const BRAND_ORANGE_DARK = "#db3f1e";
const WHITE = "#FFFFFF";
const ALPHA = (v) => `rgba(255,255,255,${v})`;

// ===== Hooks utilitarios =====
function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

function useScrollProgress() {
  const [progress, setProgress] = React.useState(0);
  React.useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      const p = max > 0 ? (h.scrollTop / max) * 100 : 0;
      setProgress(p);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return progress;
}

// ===== Item de navegación (desktop) =====
function NavItem({ to, label, active }) {
  return (
    <Button
      component={RouterLink}
      to={to}
      variant="text"
      disableRipple
      aria-current={active ? "page" : undefined}
      sx={{
        color: WHITE,
        px: 1.25,
        py: 0.75,
        mx: 0.25,
        minWidth: "auto",
        borderRadius: 1.5,
        fontWeight: 700,
        letterSpacing: 1,
        fontSize: "0.95rem",
        textTransform: "none",
        position: "relative",
        lineHeight: 1.1,
        transition: "color .25s ease, transform .25s ease",
        backgroundColor: "transparent", // sin pill
        "&:hover": {
          color: WHITE,
          transform: "scale(1.05)",
          backgroundColor: "transparent",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          left: "50%",
          bottom: 2,
          height: 2,
          width: active ? "80%" : 0,
          backgroundColor: WHITE,
          borderRadius: 999,
          transform: "translateX(-50%)",
          transition: "width .25s ease",
        },
        "&:hover::after": { width: "80%" },
        "&:focus-visible": {
          outline: "none",
          boxShadow: `0 0 0 3px ${ALPHA(0.35)}`,
          borderRadius: 2,
        },
      }}
    >
      {label}
    </Button>
  );
}

// ===== Link de Drawer (mobile) =====
function MobileLink({ onClick, label }) {
  return (
    <Button
      onClick={onClick}
      fullWidth
      sx={{
        justifyContent: "flex-start",
        color: WHITE,
        px: 1,
        py: 1.25,
        borderRadius: 1.5,
        fontWeight: 800,
        textTransform: "none",
        "&:hover": { backgroundColor: ALPHA(0.12) },
        "&:focus-visible": { outline: `2px solid ${WHITE}`, outlineOffset: 2 },
      }}
    >
      {label}
    </Button>
  );
}

// ===== Componente Principal =====
export default function Navbar() {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const scrolled = useScrolled(6);
  const progress = useScrollProgress();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  const nav = [
    { to: "/", label: "Inicio" },
    { to: "/servicios", label: "Planes" },
    { to: "/login", label: "Panel" },
  ];

  const handleWhatsAppClick = () =>
    (window.location.href = "https://wa.me/message/HOYXZTTFNXNZH1");

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <>
      {/* Barra de progreso de lectura */}
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          height: 3,
          width: `${progress}%`,
          background: WHITE,
          mixBlendMode: "overlay",
          zIndex: (t) => t.zIndex.appBar + 1,
          transition: "width .08s linear",
          pointerEvents: "none",
        }}
      />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          // ✅ SIEMPRE fondo naranja (degradado sutil)
          background: `linear-gradient(90deg, rgba(243,90,58) 0%, rgba(243,90,58,0.95) 100%)`,
          px: { xs: 1, md: 6 },
          color: WHITE,
          borderBottom: `1px solid ${ALPHA(scrolled ? 0.22 : 0)}`,
          // ✅ al scrollear aplicamos más blur, no transparencia
          backdropFilter: scrolled ? "blur(12px)" : "blur(6px)",
          transition:
            "backdrop-filter .25s ease, border-color .25s ease, background .25s ease",
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Toolbar sx={{ minHeight: 72, px: { xs: 1.5, sm: 2 } }}>
            {/* Marca */}
            <Box
              component={RouterLink}
              to="/"
              aria-label="Ir al inicio"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1.5,
                textDecoration: "none",
                color: WHITE,
                mr: 1,
                transition: "transform .25s ease, opacity .25s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  textShadow: "0 2px 12px rgba(255,255,255,0.4)",
                },
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="WebProfit"
                sx={{ height: 52, width: "auto", display: "block" }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 900,
                  letterSpacing: ".02em",
                  color: WHITE,
                  fontSize: "1.5rem",
                }}
              >
                WebProfit
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />

            {/* Desktop */}
            {isMdUp ? (
              <Stack
                direction="row"
                alignItems="center"
                spacing={2} // 👈 separación entre nav items
              >
                {nav.map((item) => (
                  <NavItem
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    active={isActive(item.to)}
                  />
                ))}

                {/* CTA Primario: Empecemos (ahora primero) */}
                <Button
                  onClick={() => {
                    setOpen(false);
                    navigate("/empecemos");
                  }}
                  endIcon={<ArrowForwardRounded />}
                  variant="contained"
                  sx={{
                    ml: 1.5,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 800,
                    color: BRAND_ORANGE_DARK,
                    bgcolor: "#FFF",
                    px: 2.5,
                    py: 1,
                    border: `1.5px solid ${ALPHA(0.6)}`,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    transition:
                      "transform .25s ease, box-shadow .25s ease, background-color .25s ease, color .25s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      bgcolor: "#F35A3A",
                      color: "#Fff",
                      boxShadow: "0 8px 20px rgba(85,85,85,0.35)",
                    },
                  }}
                >
                  Empecemos
                </Button>

                {/* CTA Secundario: WhatsApp (después) */}
                <Button
                  onClick={handleWhatsAppClick}
                  startIcon={<WhatsAppIcon sx={{ color: "inherit" }} />}
                  variant="contained"
                  sx={{
                    mx: 2,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 900,
                    bgcolor: BRAND_DARK,
                    color: "#FFF",
                    px: 2.75,
                    py: 1,
                    boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                    transition:
                      "transform .25s ease, box-shadow .25s ease, background-color .25s ease, color .25s ease",
                    "& .MuiButton-startIcon > *": {
                      color: "#25D366",
                      transition: "color .25s ease",
                    },
                    "&:hover": {
                      transform: "scale(1.05)",
                      bgcolor: "#25D366",
                      color: "#fff",
                      boxShadow: "0 10px 22px rgba(37, 211, 102, 0.35)",
                    },
                    "&:hover .MuiButton-startIcon > *": { color: "#fff" },
                  }}
                >
                  WhatsApp
                </Button>
              </Stack>
            ) : (
              <IconButton
                aria-label="Abrir menú"
                onClick={() => setOpen(true)}
                sx={{
                  color: WHITE,
                  "&:hover": { backgroundColor: ALPHA(0.12) },
                  "&:focus-visible": {
                    outline: `2px solid ${WHITE}`,
                    outlineOffset: 2,
                  },
                }}
              >
                <MenuRoundedIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Offset por AppBar fixed */}
      <Toolbar sx={{ minHeight: 72 }} />

      {/* Drawer Mobile */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "92%", sm: "86%" }, // un poco más ancho en xs
            maxWidth: 420,
            background: `linear-gradient(180deg, #1F3D60 10%, #19242F 40%)`,
            color: WHITE,
          },
        }}
      >
        {/* Header sticky: MISMA altura y paddings que la AppBar */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            minHeight: 72, // 👈 igual a la AppBar
            px: 3,
            pt: 3,
            pb: 2, // 👈 igual a la AppBar
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `1px solid ${ALPHA(0.25)}`,
            backdropFilter: "blur(6px)",
          }}
        >
          {/* Marca: mismo logo que navbar (sin cuadrito) */}
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            <Box
              component="img"
              src={logo2}
              alt="WebProfit"
              sx={{ height: 40, width: "auto", display: "block" }}
            />
            <Typography
              sx={{
                fontWeight: 900,
                color: "#f35a3a",
                fontSize: "1.5rem",
                letterSpacing: ".05em",
              }}
            >
              WebProfit
            </Typography>
          </Box>

          <IconButton
            onClick={() => setOpen(false)}
            aria-label="Cerrar menú"
            sx={{
              color: WHITE,
              "&:hover": { backgroundColor: ALPHA(0.12) },
              "&:focus-visible": {
                outline: `3px solid ${WHITE}`,
                outlineOffset: 2,
              },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        {/* Contenido */}
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 2 }}>
          {/* Nav items — tipografía y altura de toque grande */}
          <Stack spacing={0.75}>
            {[
              { label: "Inicio", to: "/" },
              { label: "Servicios", to: "/servicios" },
              { label: "Panel", to: "/login" },
            ].map((item) => (
              <Button
                key={item.to}
                onClick={() => {
                  setOpen(false);
                  if (item.to === "/contacto") {
                    navigate("/");
                    window.setTimeout(() => {
                      const target = document.getElementById("sabermas");
                      if (target) {
                        target.scrollIntoView({ behavior: "smooth", block: "start" });
                      }
                    }, 80);
                  } else {
                    navigate(item.to);
                  }
                }}
                fullWidth
                disableRipple
                sx={{
                  justifyContent: "flex-start",
                  color: WHITE,
                  px: 1,
                  py: 1.25,
                  minHeight: 52, // 👈 hit-area mobile
                  borderRadius: 1.5,
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  textTransform: "none",
                  position: "relative",
                  transition: "color .25s ease, transform .25s ease",
                  // en mobile casi no hay hover, igual dejamos feedback
                  "&:hover": { color: "#FFD700", transform: "scale(1.02)" },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    left: 12,
                    right: 12,
                    bottom: 6,
                    height: 2,
                    backgroundColor: "#FFD700",
                    borderRadius: 999,
                    transform: "scaleX(0)",
                    transformOrigin: "left center",
                    transition: "transform .25s ease",
                  },
                  "&:hover::after": { transform: "scaleX(1)" },
                  "&:focus-visible": {
                    outline: "none",
                    boxShadow: `0 0 0 3px ${ALPHA(0.35)}`,
                    borderRadius: 2,
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>

          <Stack spacing={1.5} sx={{ mt: 3 }}>
            <Button
              onClick={() => {
                setOpen(false);
                navigate("/empecemos");
              }}
              endIcon={<ArrowForwardRounded />}
              variant="contained"
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 800,
                color: BRAND_ORANGE_DARK,
                bgcolor: "#FFF",
                py: 1.6,
                fontSize: "1rem",
                border: `1.5px solid ${ALPHA(0.6)}`,
                boxShadow: "0 4px 10px rgba(0,0,0,.12)",
                "&:active": {
                  transform: "scale(0.97)",
                  boxShadow: "0 2px 6px rgba(0,0,0,.2)",
                },
                "&:focus-visible": {
                  outline: `2px solid ${WHITE}`,
                  outlineOffset: 2,
                },
              }}
            >
              EMPECEMOS
            </Button>

            <Button
              fullWidth
              onClick={() => {
                setOpen(false);
                handleWhatsAppClick();
              }}
              startIcon={<WhatsAppIcon sx={{ color: "#25D366" }} />}
              variant="contained"
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                bgcolor: BRAND_ORANGE,
                color: WHITE,
                py: 1.6, // 👈 más alto para touch
                fontSize: "1rem",
                boxShadow: "0 6px 14px rgba(0,0,0,.15)",
                "&:active": {
                  // 👈 efecto en tap
                  transform: "scale(0.97)",
                  boxShadow: "0 2px 6px rgba(0,0,0,.2)",
                },
                "&:focus-visible": {
                  outline: `2px solid ${BRAND_ORANGE}`,
                  outlineOffset: 2,
                },
              }}
            >
              Escribir por WhatsApp
            </Button>
          </Stack>

          <Typography
            variant="caption"
            sx={{
              mt: 2,
              display: "block",
              color: ALPHA(0.9),
              opacity: 1,
            }}
          >
            Respuesta en menos de 1 hora • Lun–Vie 9–18h
          </Typography>
        </Box>
      </Drawer>
    </>
  );
}
